import { useId, useRef, type ReactNode } from "react";

import { cx } from "class-variance-authority";
import {
    Area,
    AreaChart,
    CartesianGrid,
    Dot,
    ReferenceDot,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

import { useElementSize } from "../../hooks/useElementSize";
import { rawSpacing } from "../../styles/theme.css";
import { buildAxisTickLabels, getLabelStride } from "../../utils/chartAxis";
import { PendoProps } from "../../utils/pendo";

import * as theme from "./TrendChart.css";
import { TrendChartTooltip } from "./TrendChartTooltip";
import { TrendChartBucket, TrendChartSeries } from "./types";

// Namespaced so the x axis key cannot collide with a series key.
const BUCKET_LABEL_KEY = "trendChartBucketLabel";

const CHART_MARGIN = {
    top: rawSpacing[4],
    right: rawSpacing[2],
    bottom: 0,
    left: 0,
};
const Y_AXIS_WIDTH = 44;
const HIGHLIGHT_BAND_MAX_WIDTH = 52;
// Half a band, so a highlight on the first or last bucket is not clipped.
const EDGE_PADDING = HIGHLIGHT_BAND_MAX_WIDTH / 2;
const HIGHLIGHT_BAND_OPACITY = 0.12;
const HIGHLIGHT_RULE_WIDTH = 3.5;
const LINE_WIDTH = 2.5;
const DOT_RADIUS = 4;
const ACTIVE_DOT_RADIUS = 5;
const BADGE_HEIGHT = 20;
const BADGE_RADIUS = 6;
const BADGE_PADDING = rawSpacing[2];
const BADGE_GAP = rawSpacing[2];
// SVG cannot measure text before it paints, so badge width is approximated.
const BADGE_CHAR_WIDTH = 7;

export interface TrendChartProps {
    series: TrendChartSeries[];
    data: TrendChartBucket[];
    /** Index into `data`. Renders a tinted band, a vertical rule and a value badge. */
    highlightedIndex?: number;
    /** Band and rule color. Defaults to the first series color. */
    highlightColor?: string;
    height?: number;
    onBucketClick?: (index: number) => void;
    renderTooltip?: (
        bucket: TrendChartBucket,
        series: TrendChartSeries[]
    ) => ReactNode;
}

export function TrendChart({
    series,
    data,
    highlightedIndex,
    highlightColor,
    height = 260,
    onBucketClick,
    renderTooltip,
}: TrendChartProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const { width } = useElementSize(containerRef);
    // useId contains colons, which are awkward inside url(#…) references.
    const gradientPrefix = useId().replace(/:/g, "");

    const rows = data.map((bucket) => ({
        [BUCKET_LABEL_KEY]: bucket.label,
        ...Object.fromEntries(
            series.map(({ key }) => [key, bucket.values[key] ?? 0])
        ),
    }));

    const maxValue = Math.max(
        0,
        ...data.flatMap((bucket) =>
            series.map(({ key }) => bucket.values[key] ?? 0)
        )
    );
    // An all-zero dataset would otherwise collapse the axis onto one tick.
    const yAxisMax = maxValue === 0 ? 1 : "auto";

    const plotWidth = Math.max(
        0,
        (width ?? 0) - Y_AXIS_WIDTH - CHART_MARGIN.left - CHART_MARGIN.right
    );

    const bucketSpacing =
        data.length > 1 ? plotWidth / (data.length - 1) : plotWidth;
    const bandWidth = Math.min(HIGHLIGHT_BAND_MAX_WIDTH, bucketSpacing);

    const pointSpacing =
        data.length > 1
            ? (plotWidth - 2 * EDGE_PADDING) / (data.length - 1)
            : plotWidth;
    const labels = data.map(({ label }) => label);
    const labelStride = getLabelStride(labels, pointSpacing);

    const highlightedBucket =
        highlightedIndex === undefined ? undefined : data[highlightedIndex];
    const highlightStroke = highlightColor ?? series[0]?.color;
    const plotRight = (width ?? 0) - CHART_MARGIN.right;

    return (
        <div className={theme.container} ref={containerRef}>
            <ResponsiveContainer width="100%" height={height}>
                <AreaChart data={rows} margin={CHART_MARGIN}>
                    <defs>
                        {series.map(({ key, color }) => (
                            <linearGradient
                                key={key}
                                id={`${gradientPrefix}${key}`}
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <stop
                                    offset="0%"
                                    stopColor={color}
                                    stopOpacity={0.22}
                                />
                                <stop
                                    offset="100%"
                                    stopColor={color}
                                    stopOpacity={0.01}
                                />
                            </linearGradient>
                        ))}
                    </defs>
                    <CartesianGrid vertical={false} />
                    <XAxis
                        dataKey={BUCKET_LABEL_KEY}
                        type="category"
                        axisLine={false}
                        tickLine={false}
                        interval={0}
                        ticks={buildAxisTickLabels(labels, labelStride, [
                            data.length - 1,
                            highlightedIndex,
                        ])}
                        padding={{ left: EDGE_PADDING, right: EDGE_PADDING }}
                        tick={
                            <TrendChartAxisTick
                                activeLabel={highlightedBucket?.label}
                            />
                        }
                    />
                    <YAxis
                        width={Y_AXIS_WIDTH}
                        domain={[0, yAxisMax]}
                        allowDecimals={false}
                        axisLine={false}
                        tickLine={false}
                        // Short numeric labels never collide, so skip
                        // recharts' overlap avoidance.
                        interval={0}
                        className={theme.axisTick}
                    />
                    {highlightedBucket !== undefined && (
                        <ReferenceLine
                            x={highlightedBucket.label}
                            stroke={highlightStroke}
                            strokeWidth={bandWidth}
                            strokeOpacity={HIGHLIGHT_BAND_OPACITY}
                        />
                    )}
                    {highlightedBucket !== undefined && (
                        <ReferenceLine
                            x={highlightedBucket.label}
                            stroke={highlightStroke}
                            strokeWidth={HIGHLIGHT_RULE_WIDTH}
                        />
                    )}
                    <Tooltip
                        content={
                            <TrendChartTooltip
                                series={series}
                                renderContent={renderTooltip}
                            />
                        }
                    />
                    {series.map(
                        ({ key, label, color, "data-pendo": dataPendo }) => (
                            <Area
                                key={key}
                                type="linear"
                                dataKey={key}
                                name={label}
                                stroke={color}
                                strokeWidth={LINE_WIDTH}
                                fill={`url(#${gradientPrefix}${key})`}
                                dot={
                                    <TrendChartDot
                                        data-pendo={dataPendo}
                                        color={color}
                                        onBucketClick={onBucketClick}
                                    />
                                }
                                activeDot={
                                    <TrendChartDot
                                        data-pendo={dataPendo}
                                        color={color}
                                        onBucketClick={onBucketClick}
                                        active
                                    />
                                }
                                // Animation replays on every resize, which reads as a glitch.
                                isAnimationActive={false}
                            />
                        )
                    )}
                    {highlightedBucket !== undefined &&
                        series.map(({ key, color }) => (
                            <ReferenceDot
                                key={key}
                                x={highlightedBucket.label}
                                y={highlightedBucket.values[key] ?? 0}
                                shape={
                                    <TrendChartValueBadge
                                        label={String(
                                            highlightedBucket.values[key] ?? 0
                                        )}
                                        color={color}
                                        plotRight={plotRight}
                                    />
                                }
                            />
                        ))}
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
}

interface TrendChartDotProps extends PendoProps {
    cx?: number;
    cy?: number;
    index?: number;
    color: string;
    active?: boolean;
    onBucketClick?: (index: number) => void;
}

function TrendChartDot({
    cx: centerX,
    cy: centerY,
    index,
    color,
    active = false,
    "data-pendo": dataPendo,
    onBucketClick,
}: TrendChartDotProps) {
    return (
        <Dot
            data-pendo={dataPendo}
            cx={centerX}
            cy={centerY}
            r={active ? ACTIVE_DOT_RADIUS : DOT_RADIUS}
            fill={color}
            strokeWidth={active ? 2 : 0}
            className={cx(
                active && theme.activeDot,
                onBucketClick !== undefined && theme.clickableDot
            )}
            onClick={
                onBucketClick === undefined
                    ? undefined
                    : () => {
                          if (index !== undefined) {
                              onBucketClick(index);
                          }
                      }
            }
        />
    );
}

interface TrendChartAxisTickProps {
    activeLabel?: string;
    // Supplied by recharts when it clones this element.
    x?: number;
    y?: number;
    payload?: { value?: string };
}

function TrendChartAxisTick({
    activeLabel,
    x = 0,
    y = 0,
    payload,
}: TrendChartAxisTickProps) {
    const isActive =
        payload?.value !== undefined && payload.value === activeLabel;
    return (
        <text
            x={x}
            y={y}
            dy={12}
            className={cx(
                theme.axisTick,
                theme.axisTickHorizontal,
                isActive && theme.axisTickActive
            )}
        >
            {payload?.value}
        </text>
    );
}

interface TrendChartValueBadgeProps {
    label: string;
    color: string;
    plotRight: number;
    // Supplied by recharts when it clones this element.
    cx?: number;
    cy?: number;
}

function TrendChartValueBadge({
    label,
    color,
    plotRight,
    cx: centerX = 0,
    cy: centerY = 0,
}: TrendChartValueBadgeProps) {
    const width = label.length * BADGE_CHAR_WIDTH + BADGE_PADDING * 2;
    // Flips to the left of the point when there is no room on the right.
    const fitsRight = centerX + BADGE_GAP + width <= plotRight;
    const x = fitsRight ? centerX + BADGE_GAP : centerX - BADGE_GAP - width;
    return (
        <g>
            <rect
                x={x}
                y={centerY - BADGE_HEIGHT / 2}
                width={width}
                height={BADGE_HEIGHT}
                rx={BADGE_RADIUS}
                fill={color}
            />
            <text
                x={x + width / 2}
                y={centerY}
                className={theme.valueBadgeText}
            >
                {label}
            </text>
        </g>
    );
}
